import React, { useState, useEffect } from 'react';
import { getArbicsxProfits, getArbicsxSettings } from '../../lib/api';
import { useModule } from '../../contexts/ModuleContext';
import { formatCurrency, formatCurrencyColored, formatDate, getMonthName } from '../../lib/utils';
import { PropFirmLogo } from '../../lib/propFirmLogos';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { RefreshCw, TrendingUp, Calendar, List, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationControls } from '../../components/ui/pagination-controls';

const sourceLabel = (source) =>
  source === 'prop_payout_burn_15' ? 'Profitto Prop' :
  source === 'broker_profit' ? 'Profitto Broker' :
  source === 'prop_income' ? 'Incasso Prop (Bonus 15%)' :
  source === 'cycle_complete' ? 'Completamento Conto' :
  source || '-';

export default function ArbicsxProfitti() {
  const { impersonatedUser, isImpersonating } = useModule();
  const [profits, setProfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [openGroups, setOpenGroups] = useState({});

  const impersonateId = isImpersonating ? impersonatedUser?.id : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const profitsRes = await getArbicsxProfits(impersonateId, page, 100);
      const profitsData = profitsRes.data;
      setProfits(profitsData.items || []);
      setPagination({ total: profitsData.total || 0, pages: profitsData.pages || 1 });
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [impersonateId, page]);

  const totalProfit = profits.reduce((sum, p) => sum + (p.gross_amount || 0), 0);

  // Group by month
  const profitsByMonth = profits.reduce((acc, p) => {
    const month = p.date?.substring(0, 7) || 'unknown';
    if (!acc[month]) acc[month] = [];
    acc[month].push(p);
    return acc;
  }, {});

  // Group by week
  const profitsByWeek = profits.reduce((acc, p) => {
    const date = new Date(p.date);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay() + 1);
    const weekKey = weekStart.toISOString().split('T')[0];
    if (!acc[weekKey]) acc[weekKey] = [];
    acc[weekKey].push(p);
    return acc;
  }, {});

  const toggleGroup = (key) => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const renderProfitRows = (list) => (
    <div className="ht-list">
      {list.map(profit => (
        <div key={profit.id} className="ht-item">
          <div className="ht-item-main">
            <p className="ht-item-t flex items-center gap-2">
              <PropFirmLogo name={profit.prop_firm} size="xs" />
              <span className="truncate">{profit.prop_firm || '-'}</span>
            </p>
            <p className="ht-item-s">{formatDate(profit.date)} · {sourceLabel(profit.source)}</p>
          </div>
          <span className={`ht-item-n ${(profit.gross_amount || 0) >= 0 ? 'ht-pos' : 'ht-alert'}`}>
            {formatCurrencyColored(profit.gross_amount || 0).text}
          </span>
        </div>
      ))}
    </div>
  );

  const renderAccordion = (keys, groups, titleFor, keyPrefix) => (
    <div className="space-y-2">
      {keys.length === 0 ? (
        <div className="ht-panel ht-empty">Nessun profitto registrato</div>
      ) : (
        keys.map(key => {
          const list = groups[key];
          const total = list.reduce((s, p) => s + (p.gross_amount || 0), 0);
          const open = !!openGroups[`${keyPrefix}-${key}`];
          return (
            <div key={key} className="ht-acc">
              <button
                type="button"
                className="ht-acc-head"
                onClick={() => toggleGroup(`${keyPrefix}-${key}`)}
                data-testid={`profit-group-${keyPrefix}-${key}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <ChevronRight size={14} className={`ht-chev ${open ? 'ht-chev-open' : ''}`} />
                  <span className="ht-acc-title truncate">{titleFor(key)}</span>
                  <span className="ht-tag">{list.length}</span>
                </span>
                <span className={`ht-item-n ${total >= 0 ? 'ht-pos' : 'ht-alert'}`}>
                  {formatCurrencyColored(total).text}
                </span>
              </button>
              {open && <div className="ht-acc-body">{renderProfitRows(list)}</div>}
            </div>
          );
        })
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">Storico Profitti</h1>
          <p className="ht-page-sub">
            Arbicsx {isImpersonating && `- ${impersonatedUser?.name}`}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="ht-btn self-start sm:self-auto"
          disabled={loading}
          data-testid="refresh-profits-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Aggiorna
        </button>
      </div>

      {/* Totale */}
      <div className="ht-panel">
        <div className="ht-hero">
          <div className="ht-hero-main">
            <span className="ht-hero-label">Totale profitti netti</span>
            <p className={`ht-hero-num ${totalProfit >= 0 ? 'ht-pos' : 'ht-alert'}`} data-testid="profits-total">
              {formatCurrencyColored(totalProfit).text}
            </p>
          </div>
          <div className="ht-hero-side ht-rows">
            <div className="ht-row">
              <span className="ht-row-label"><TrendingUp size={12} /> Voci in pagina</span>
              <span className="ht-row-value">{profits.length}</span>
            </div>
            <div className="ht-row">
              <span className="ht-row-label">Mesi coperti</span>
              <span className="ht-row-value">{Object.keys(profitsByMonth).length}</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="monthly" className="w-full">
        <TabsList className="ht-tabs bg-transparent border-0 p-0 h-auto">
          <TabsTrigger value="monthly" className="ht-tab">
            <Calendar size={13} /> Mensile
          </TabsTrigger>
          <TabsTrigger value="weekly" className="ht-tab">
            <Calendar size={13} /> Settimanale
          </TabsTrigger>
          <TabsTrigger value="list" className="ht-tab">
            <List size={13} /> Lista
          </TabsTrigger>
        </TabsList>

        {/* Mensile: accordion con subtotale nell'intestazione */}
        <TabsContent value="monthly" className="mt-4">
          {renderAccordion(
            Object.keys(profitsByMonth).sort().reverse(),
            profitsByMonth,
            (m) => getMonthName(m),
            'm'
          )}
        </TabsContent>

        {/* Settimanale */}
        <TabsContent value="weekly" className="mt-4">
          {renderAccordion(
            Object.keys(profitsByWeek).sort().reverse(),
            profitsByWeek,
            (weekStart) => {
              const weekEnd = new Date(weekStart);
              weekEnd.setDate(weekEnd.getDate() + 6);
              return `${formatDate(weekStart)} - ${formatDate(weekEnd.toISOString().split('T')[0])}`;
            },
            'w'
          )}
        </TabsContent>

        {/* Lista */}
        <TabsContent value="list" className="mt-4">
          <div className="ht-panel overflow-hidden">
            {profits.length === 0 ? (
              <div className="ht-empty">
                Nessun profitto registrato. I profitti vengono generati automaticamente dai conti.
              </div>
            ) : (
              renderProfitRows(profits)
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
